/**
 * CustomEtapasLista.tsx — o acompanhamento como LISTA VERTICAL.
 *
 * O CARD é o do fórum do tema, copiado classe por classe de
 * `theme/adminux-react/src/pages/forum/Forum.tsx` (lista) e `ForumDetails.tsx`
 * (rodapé com o campo de comentário). Não se redesenha: o título é `h4` escuro,
 * a linha de meta é avatar + "De quem é", o corpo preenche, as tags ficam
 * abaixo do texto, o rodapé é a faixa de números e o input encostado.
 *
 * A LINHA (uma etapa larga, duas lado a lado) continua sendo o "quando": o que
 * pode acontecer ao mesmo tempo divide a mesma linha. Isso não é o card — é o
 * arranjo das linhas.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { mensagensApi } from '@/lib/mensagens';
import { ApiError } from '@/lib/api';
import { TextoComMencoes } from '@/components/CustomMensagens';
import { formatarData, tempoRelativo } from '@/lib/data';
import type { Etapa } from './CustomEtapas';
import type { UsuarioOpcao } from './shared';
import { etapaAberta } from './shared';

interface Props {
  etapas: Etapa[];
  /** Ativos, com foto — de onde saem os rostos de quem acompanha. */
  usuarios: UsuarioOpcao[];
  autorizacaoId: number;
  podeGerir: boolean;
  meuId?: number | null;
  /** Recarrega a lista depois de qualquer escrita. */
  aoMudar: () => void;
  /** "Próxima etapa" no rodapé do card — o pai abre o painel de criação. */
  aoDerivar: (etapa: Etapa) => void;
  /** @deprecated saiu com o botão Assumir em 12/09 — ver o card. */
  /** Concluir / adiar / reabrir / excluir — o pai é quem chama a API. */
  aoConcluir: (etapa: Etapa) => void;
  aoAdiar: (etapa: Etapa) => void;
  aoReabrir: (etapa: Etapa) => void;
  aoExcluir: (etapa: Etapa) => void;
  /** Injetado DENTRO do card (o formulário de concluir) e DEPOIS da linha (o de criar). */
  slotNoCard?: (etapa: Etapa) => React.ReactNode;
  slotDepoisDaLinha?: (nivel: number, etapasDaLinha: Etapa[]) => React.ReactNode;
}

/** As etapas agrupadas por LINHA: o nível é 1 + o maior nível de quem ela espera. */
export function porLinha(etapas: Etapa[]): Etapa[][] {
  const porId = new Map(etapas.map((e) => [e.id, e]));
  const nivel = new Map<number, number>();

  const calcular = (e: Etapa, visitando: Set<number>): number => {
    if (nivel.has(e.id)) return nivel.get(e.id)!;
    // Ciclo não deveria existir (o backend recusa); se existir, a tela não pode
    // travar num laço — para no primeiro reencontro.
    if (visitando.has(e.id)) return 0;
    visitando.add(e.id);
    const anteriores = e.depende_de.map((id) => porId.get(id)).filter(Boolean) as Etapa[];
    const n = anteriores.length ? Math.max(...anteriores.map((a) => calcular(a, visitando))) + 1 : 0;
    visitando.delete(e.id);
    nivel.set(e.id, n);
    return n;
  };

  etapas.forEach((e) => calcular(e, new Set()));
  const linhas: Etapa[][] = [];
  etapas.forEach((e) => {
    const n = nivel.get(e.id) ?? 0;
    (linhas[n] ||= []).push(e);
  });
  return linhas.filter(Boolean);
}

/** 1.0 · 2.1 · 2.2 — o número da etapa, e a derivação dentro dele.
 *
 * Regra dele, 04/09/2026: antes do ponto vai A VEZ (o degrau — quem só começa
 * quando o degrau anterior fecha está no seguinte); depois do ponto vai O RAMO
 * dentro do degrau, `0` quando não houve divisão. *"Se a primeira etapa vira
 * duas, elas têm que ter o mesmo número seguinte, só com um ponto e um dígito."*
 *
 * ⚠️ O que ele NÃO diz: de qual etapa de cima saiu cada ramo, quando dois
 * degraus se dividem em cadeia — isso exigiria um número que cresce a cada
 * nível (1.2.1), e ele pediu um dígito. Quem responde isso é o corpo, por nome.
 *
 * ⛔ A FORMA de mostrar este número está PARADA NELE (04/09): o texto cinza e o
 * quadrado do tema foram os dois recusados — *"não ficou bom, vamos pensar numa
 * forma melhor; eu vou dar uma pesquisada no tema"*. Até ele trazer, o número
 * fica no badge, como está aqui. NÃO invente outra. */
function numeroDaEtapa(linha: number, posicao: number, quantasNaLinha: number): string {
  return `${linha + 1}.${quantasNaLinha > 1 ? posicao + 1 : 0}`;
}

/** O número de CADA etapa, por id — para quem precisa numerar fora desta lista.
 *
 * A linha do tempo da autorização mostra "Etapa 2.0 · Verificar Cobrança OS 35",
 * e o número tem de ser o MESMO do card: dois números para a mesma etapa seria
 * pior do que nenhum. Por isso ele sai daqui, e não de uma segunda conta. */
export function numerosDasEtapas(etapas: Etapa[]): Map<number, string> {
  const mapa = new Map<number, string>();
  porLinha(etapas).forEach((linha, i) => {
    linha.forEach((e, j) => mapa.set(e.id, numeroDaEtapa(i, j, linha.length)));
  });
  return mapa;
}

/* ⛔ O SELO É O DA CASA (`casaa-selo-*`), NUNCA O `theme-{cor}` DO TEMA.
   Estado é INFORMAÇÃO, e a lei visual (doc 11) manda informação ter cor fixa e
   nossa: se o "Feita" mudasse de cor porque alguém trocou o tema, ele deixaria
   de significar alguma coisa.

   E há o motivo medido, 04/09/2026: com `badge-light text-bg-theme-1
   theme-green` o "Feita" dava **2,5 de contraste** — verde sobre verde, e ele
   não conseguia ler. Os selos da casa (`_selos.scss` + `_cores.scss`) têm o par
   texto/fundo calculado e o bloco escuro declarado. Medidos: 4,6 a 5,3.

   O medidor é `.claude/src/scripts/selos_contraste.py` — rode-o antes de dizer
   que um selo está bom, porque a olho um verde claro parece legível. */
/* ⛔ SÃO DOIS EIXOS, E CADA UM TEM O SEU SELO — corrigido em 05/09/2026,
   depois de ele topar com a incoerência: assumir a etapa e depois ver
   "Não iniciada" no lugar de "Liberada" confundia, porque as duas eram a mesma
   coisa dita de dois jeitos.

     *"Cabe 'Não iniciada' e uma tag 'Liberada'. Não fica claro na hora que a
     pessoa vai mexer… você precisa ter clareza."*

   ESTADO — o que a etapa é, e o que a pessoa escolhe:
     Não iniciada · Em andamento · Concluída · Dispensada

   FILA — a situação dela na ordem, CALCULADA das dependências e nunca
   escolhida à mão:
     Bloqueada (espera outra) · Liberada (pode ser assumida)

   Só faz sentido enquanto a etapa não começou: depois de assumida, quem manda
   é o estado. E é por isso que o kanban terá QUATRO colunas, não cinco —
   ninguém arrasta um cartão para "bloqueada". */
function estadoDaEtapa(e: Etapa) {
  if (e.estado === 'feita') return { chave: 'feita', rotulo: 'Concluída', cor: 'casaa-selo-sucesso', icone: 'bi-check-lg' };
  if (e.estado === 'dispensada') return { chave: 'dispensada', rotulo: 'Dispensada', cor: 'casaa-selo-neutro', icone: 'bi-dash-lg' };
  if (e.estado === 'em_andamento') return { chave: 'em-andamento', rotulo: 'Em andamento', cor: 'casaa-selo-destaque', icone: 'bi-play-fill' };
  return { chave: 'nao-iniciada', rotulo: 'Não iniciada', cor: 'casaa-selo-neutro', icone: 'bi-circle' };
}

/** A TAG DA FILA — só enquanto a etapa não começou. */
function filaDaEtapa(e: Etapa) {
  if (e.estado !== 'aberta') return null;
  return e.na_vez
    ? { rotulo: 'Liberada', cor: 'casaa-selo-info' }
    : { rotulo: 'Bloqueada', cor: 'casaa-selo-atencao' };
}

/** O prazo vencido — alerta que ATRAVESSA os estados, não um deles. */
function alertaDoPrazo(e: Etapa) {
  return e.vencida && e.estado !== 'feita' && e.estado !== 'dispensada';
}

/** O parágrafo que preenche o card — sem ele o fórum vira formulário oco.
 *
 * ⛔ SEM FRASE DEDUZIDA. Quando ninguém explicou a etapa, o card mostra três
 * pontinhos (ver o `<p>` lá embaixo) e não uma frase que o sistema inventou:
 * *"se ela não escreveu nada, é só 'entrar em contato com a PC' — ela tem que
 * explicar alguma coisa, isso tem que aparecer"* (04/09). O vazio visível é o
 * que cobra a explicação; a frase bonita o esconderia.
 *
 * A última mensagem também não serve de corpo: ela já aparece na prévia do
 * rodapé, e repetida aqui o card dizia a mesma coisa duas vezes. */
function corpoDaEtapa(e: Etapa): string | null {
  const detalhe = e.detalhe?.trim();
  if (detalhe) return detalhe;
  const desfecho = e.observacao_conclusao?.trim();
  if (desfecho) return desfecho;
  /* A espera NÃO entra aqui: ela tem linha própria no card desde 05/09, e
     escrevê-la nos dois lugares dizia a mesma coisa duas vezes. O corpo é da
     EXPLICAÇÃO — e o vazio dele (os três pontinhos) é o que cobra que alguém
     explique a etapa. */
  return null;
}

/** A última vez que alguém mexeu — é o "1 hr ago" do fórum.
 *
 * A criação servia enquanto tudo era semeado junto: quatro cards dizendo
 * "há 3 h" não informam nada. */
function mexidaEm(e: Etapa): string | null {
  return e.conversa?.ultimas?.at(-1)?.criacao ?? e.conclusao ?? e.criado_em ?? null;
}

/** QUEM ACOMPANHA a etapa — os rostos da faixa, no lugar do "Liked by".
 *
 * Não são os autores das mensagens, que era o desenho anterior: ele pediu
 * (04/09/2026) que o rosto dele e o da Adriana apareçam SEMPRE, *"porque a
 * gente está visualizando aquilo, então a gente está envolvido"* — e porque sem
 * eles o card fica vazio demais.
 *
 * A regra não cita nome: acompanha quem é **autorizador** (hoje os dois), mais
 * o responsável da etapa, mais quem já falou nela. Quando a etapa ganhar
 * participantes de verdade no motor de mensagens (#10), esta função troca de
 * fonte sem mudar de forma. */
function rostosQueAcompanham(e: Etapa, usuarios: UsuarioOpcao[]) {
  const vistos = new Set<string>();
  const saida: { chave: string; foto?: string | null; nome?: string | null }[] = [];
  const por = (chave: string, foto?: string | null, nome?: string | null) => {
    if (!chave || vistos.has(chave)) return;
    vistos.add(chave);
    saida.push({ chave, foto, nome });
  };
  usuarios.filter((u) => u.autorizador).forEach((u) => por(`u${u.id}`, u.foto, u.nome));
  if (e.responsavel_id) por(`u${e.responsavel_id}`, e.responsavel_foto, e.responsavel_nome);
  for (const m of e.conversa?.ultimas ?? []) {
    if (m.origem !== 'pessoa') continue;
    por(m.autor_nome ?? `m${m.id}`, m.autor_foto, m.autor_nome);
  }
  return saida.slice(0, 3);
}

/* ═══════════════════════════════════════════════════════════════════════════
 * O RODAPÉ — copiado de ForumDetails.tsx (faixa de números + o input)
 * ═══════════════════════════════════════════════════════════════════════════ */
function RodapeDoCard({
  etapa,
  usuarios,
  autorizacaoId,
  podeGerir,
  aoMudar,
  aoDerivar,
}: {
  etapa: Etapa;
  usuarios: UsuarioOpcao[];
  autorizacaoId: number;
  podeGerir: boolean;
  aoMudar: () => void;
  aoDerivar: (etapa: Etapa) => void;
}) {
  const [resposta, setResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const conversa = etapa.conversa ?? null;
  const link = `/autorizacoes/${autorizacaoId}/etapas/${etapa.id}`;
  const rostos = rostosQueAcompanham(etapa, usuarios);

  /* ⛔ A LINHA DE SISTEMA NÃO É CONVERSA. "Vittorio abriu a sala" ocupava o
     lugar de uma fala de verdade — *"não tem nada que botar que Vittorio abriu
     a sala; o registro é importante, mas fica lá dentro, quem abriu é
     irrelevante"* (04/09). Só falas de PESSOA contam e sobem para o card. */
  const falas = (conversa?.ultimas ?? []).filter((m) => m.origem === 'pessoa');
  const total = falas.length;
  /* UMA fala, não duas — dele, 05/09: *"você só mostra a última mensagem que
     chegou, se tiver um chat; vamos reduzir isso porque ficou muito grande"*.
     O card do acompanhamento é um resumo: quem quer a conversa abre a etapa. */
  const previa = falas.slice(-1);

  /* RESPONDER SEM SAIR DA LISTA — pedido dele, 04/09/2026: *"se a sala já
     tiver aberta, abre um espaço embaixo para a pessoa responder direto,
     porque ela não precisa entrar no cartão: ela já vê as últimas duas
     mensagens e, se achar que basta, responde dali"*.

     Só com o chat ABERTO. Iniciar o chat continua sendo ato da página da
     etapa — aqui não se cria nada, só se continua. */
  const podeResponder = !!conversa && conversa.posso_falar;

  async function responder() {
    const t = resposta.trim();
    if (!t || enviando || !conversa) return;
    setEnviando(true);
    try {
      await mensagensApi.enviar(conversa.sala_id, { texto: t });
      setResposta('');
      aoMudar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para enviar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="card-footer casaa-etapa-rodape mt-auto">
      <div className="row gx-3 align-items-center">
        {/* "REGISTROS" NÃO DIZIA DE QUÊ — dele, 05/09: *"registros de quê? A
            gente tem o chat e essas atividades"*. São dois números, cada um com
            o seu nome, e ficam visíveis mesmo em zero: a ausência informa. */}
        <div className="col-auto">
          <p className="text-secondary small mb-0">Atividades</p>
          <h5 className="mb-0">{etapa.total_atividades ?? 0}</h5>
        </div>
        <div className="col-auto">
          <p className="text-secondary small mb-0">Chat</p>
          <h5 className="mb-0">{total}</h5>
        </div>
        {/* `avatar-group` é a peça do tema para fila de rostos: ela SOBREPÕE os
            avatares (-4px por dezena de tamanho), e é o que faz três fotos de
            50px caberem num card de meia largura sem empurrar o "Abrir" para
            uma linha só dele. */}
        {/* Os rostos somem abaixo de `xl` no card de meia largura: ali a linha
            não cabe com eles mais os dois botões, e era isso que jogava o
            "Abrir" para uma linha própria (medido em 05/09). */}
        {rostos.length > 0 && (
          <div className="col-auto avatar-group d-none d-xl-flex">
            {rostos.map((r) => (
              r.foto ? (
                <div className="avatar avatar-50 rounded-circle casaa-foto-no-avatar" key={r.chave}>
                  <img src={r.foto} alt="" />
                </div>
              ) : (
                <div
                  className="avatar avatar-50 rounded-circle bg-theme-1-subtle text-theme-1"
                  key={r.chave}
                  title={r.nome ?? undefined}
                >
                  <i className="bi bi-person"></i>
                </div>
              )
            ))}
          </div>
        )}
        <div className="col">
          {conversa && conversa.nao_lidas > 0 && (
            <>
              <p className="text-secondary small mb-0">Não lidas</p>
              <h5>{conversa.nao_lidas}</h5>
            </>
          )}
        </div>
        {/* AS DUAS AÇÕES DO CARD FICAM JUNTAS, na faixa. "Próxima etapa" ficou
            um tempo solto abaixo da atividade e dava a impressão errada — dele,
            05/09: *"ali dá a impressão de que você vai registrar outra
            atividade; tem que ser alinhado ao Abrir"*. Abrir é a ação de LER
            (sólida); criar a próxima é a de CONSTRUIR a corrente (contornada),
            e some quando a etapa já fechou. */}
        {/* ASSUMIR — é o gesto que o kanban vai representar arrastando o
            cartão de Liberada para Em andamento. Só aparece na etapa liberada:
            bloqueada não começa, e quem manda ali é a dependência.

            ⛔ E SÓ PARA O RESPONSÁVEL (dele, 05/09): *"o botão de assumir só
            deveria estar disponível para quem é o responsável; depois você pode
            até trocar"*. Começar diz "esta é a minha vez", e a vez é de quem
            responde pela etapa — não de quem clica. Sem responsável, aparece
            para todos, porque aí assumir é PEGAR PARA SI. A trava de verdade
            está no serviço; aqui só se esconde o que ia dar 403. */}
        {/* AS AÇÕES ANDAM JUNTAS, e no CELULAR ocupam uma linha inteira.
            Cada botão era um `col-auto` solto: em 390px a linha não cabia e o
            "Abrir" caía sozinho POR BAIXO dos números, longe do "Próxima
            etapa" — exatamente o desalinhamento que ele já tinha reprovado no
            desktop em 05/09. Agrupados, ou cabem todos na faixa (≥ sm) ou
            descem todos juntos, lado a lado (medido em 10/09). */}
        <div className="col-12 col-sm-auto ms-sm-auto mt-2 mt-sm-0
                        d-flex flex-wrap gap-2 justify-content-end">
          {/* ⛔ NÃO VOLTE COM O "ASSUMIR" — saiu em 12/09. Pôr em andamento é
              mudar o campo Situação, na página da etapa. */}
          {podeGerir && (etapa.estado === 'aberta' || etapa.estado === 'em_andamento') && (
            <button type="button" className="btn btn-md btn-outline-theme"
                    onClick={() => aoDerivar(etapa)}>
              <i className="bi bi-plus-lg me-1"></i>Próxima etapa
            </button>
          )}
          <Link to={link} className="btn btn-theme btn-md">Abrir</Link>
        </div>
      </div>
      {/* A ÚLTIMA FALA E A RESPOSTA.
          O bloco é o COMENTÁRIO do `ForumDetails.tsx`, copiado classe por
          classe: `row gx-3 mt-3` (é ele que dá o respiro entre uma fala e
          outra), avatar de 40 com `shadow-sm`, nome como LINK e o tempo em
          `small` cinza ao lado, e o texto num `<p>` normal — não um `small`
          cinza espremido, que foi o que ele reprovou em 04/09. O campo é o
          `input-group` do mesmo arquivo: `form-control border` + `btn btn-light
          border`. */}
      {(previa.length > 0 || podeResponder) && (
        <div className="border-top mt-3 pt-2">
          {previa.map((m) => (
            <div className="row gx-3 mt-3" key={m.id}>
              <div className="col-auto">
                {m.autor_foto ? (
                  <figure className="avatar avatar-40 rounded-circle casaa-foto-no-avatar vm shadow-sm mb-0">
                    <img src={m.autor_foto} alt="" />
                  </figure>
                ) : (
                  <span className="avatar avatar-40 rounded-circle bg-theme-1-subtle text-theme-1 vm shadow-sm">
                    <i className="bi bi-person"></i>
                  </span>
                )}
              </div>
              <div className="col">
                <p className="mb-1">
                  <Link to={link} className="style-none">{m.autor_nome}</Link>
                  {m.criacao && (
                    <span className="text-secondary d-inline-block ms-2 small">{tempoRelativo(m.criacao)}</span>
                  )}
                </p>
                <p className="casaa-etapa-chat-texto mb-0">
                  <TextoComMencoes texto={m.texto} mencoes={usuarios} />
                </p>
              </div>
            </div>
          ))}
          {podeResponder && (
            <div className="input-group mt-3">
              <input
                type="text"
                className="form-control border"
                placeholder="Responda daqui mesmo…"
                value={resposta}
                onChange={(ev) => setResposta(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    responder();
                  }
                }}
                maxLength={2000}
              />
              <button
                className="btn btn-light border"
                type="button"
                onClick={responder}
                disabled={enviando}
              >
                {enviando ? <span className="spinner-border spinner-border-sm" /> : 'Responder'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * O CARD DA ETAPA — markup de Forum.tsx + rodapé de ForumDetails.tsx
 * ═══════════════════════════════════════════════════════════════════════════ */
function Cartao({
  etapa,
  numero,
  esperando,
  usuarios,
  autorizacaoId,
  podeGerir,
  meuId,
  aoMudar,
  aoDerivar,
  aoConcluir,
  aoAdiar,
  aoReabrir,
  aoExcluir,
  slotNoCard,
}: {
  etapa: Etapa;
  numero: string;
  /** As etapas que ela espera, COM O NÚMERO — "Espera 1.0 'Negociar com a
   *  Hilza'". Pedido dele em 05/09: sem o número, quem lê tem de procurar na
   *  lista qual é a etapa citada. */
  esperando: { numero: string; titulo: string }[];
} & Omit<Props, 'etapas' | 'slotDepoisDaLinha'>) {
  const st = estadoDaEtapa(etapa);
  const fila = filaDaEtapa(etapa);
  const eu = etapa.responsavel_id === meuId;
  const link = `/autorizacoes/${autorizacaoId}/etapas/${etapa.id}`;
  const corpo = corpoDaEtapa(etapa);
  const mexida = mexidaEm(etapa);
  const quando = mexida ? tempoRelativo(mexida) : null;
  const prazoOuFim = etapa.estado !== 'aberta' && etapa.conclusao
    ? {
      rotulo: etapa.estado === 'feita' ? 'Concluída' : 'Dispensada',
      valor: formatarData(etapa.conclusao.slice(0, 10)),
    }
    : etapa.prazo
      ? {
        rotulo: 'Prazo',
        valor: formatarData(etapa.prazo),
        vencida: etapa.vencida,
      }
      : null;

  return (
    /* MESMA ALTURA NA LINHA — dele, 05/09: *"o card tem que ser sempre do
       mesmo tamanho"*. Três coisas juntas fazem isso funcionar, e faltando uma
       não funciona: `h-100` (estica até a altura da linha), `d-flex
       flex-column` + rodapé em `mt-auto` (o rodapé encosta embaixo em vez de
       abrir um vão no meio) e a MARGEM NA COLUNA, não aqui — com ela no card, o
       `h-100` mede 100% da coluna e ainda soma a margem, e os dois nunca fecham
       na mesma altura (medido: 650 contra 387). */
    <div className="card adminuiux-card casaa-etapa-card h-100 d-flex flex-column mb-0">
      <div className="card-body">
        <div className="row gx-3 align-items-center mb-3">
          <div className="col">
            {/* O NÚMERO ABRE A LINHA, no quadradinho da agenda dos
                instaladores — forma escolhida por ele (ver `_etapas.scss`).
                `bg-theme-1 text-white` é o mesmo par que a agenda usa no dia de
                hoje: decoração, acompanha o tema. */}
            <div className="d-flex align-items-center gap-2">
              <span className="casaa-etapa-numero bg-theme-1 text-white flex-shrink-0">
                {numero}
              </span>
              <h4 className="fs-3 mb-0">
                <Link to={link} className="style-none text-reset">{etapa.titulo}</Link>
              </h4>
            </div>
          </div>
          <div className="col-auto">
            <div className="dropdown">
              <button
                className="btn btn-square btn-link dropdown-toggle no-caret"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label="Mais ações"
              >
                <i className="bi bi-three-dots-vertical vm"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><Link className="dropdown-item" to={link}>Abrir a etapa</Link></li>
                {/* CONCLUIR e ADIAR valem para a etapa em andamento também —
                    comparar com 'aberta' fazia sumir do menu justamente quem
                    estava trabalhando na etapa. */}
                {podeGerir && etapaAberta(etapa) && (
                  <li>
                    <button className="dropdown-item" type="button" onClick={() => aoConcluir(etapa)}>
                      Concluir
                    </button>
                  </li>
                )}
                {podeGerir && (
                  <li>
                    <button className="dropdown-item" type="button" onClick={() => aoDerivar(etapa)}>
                      Próxima etapa
                    </button>
                  </li>
                )}
                {podeGerir && etapaAberta(etapa) && (
                  <li>
                    <button className="dropdown-item" type="button" onClick={() => aoAdiar(etapa)}>
                      Adiar uma semana
                    </button>
                  </li>
                )}
                {podeGerir && !etapaAberta(etapa) && (
                  <li>
                    <button className="dropdown-item" type="button" onClick={() => aoReabrir(etapa)}>
                      Reabrir
                    </button>
                  </li>
                )}
                {podeGerir && (
                  <>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item text-danger" type="button" onClick={() => aoExcluir(etapa)}>
                        Excluir
                      </button>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="row gx-3 align-items-center mb-3">
          <div className="col-auto">
            {etapa.responsavel_foto ? (
              <figure className="avatar avatar-50 rounded-circle casaa-foto-no-avatar vm">
                <img src={etapa.responsavel_foto} alt="" />
              </figure>
            ) : (
              <span className="avatar avatar-50 rounded-circle bg-theme-1-subtle text-theme-1 vm">
                <i className="bi bi-person"></i>
              </span>
            )}
          </div>
          <div className="col">
            <p className="small text-secondary mb-0">Responsável</p>
            <p className="mb-0">
              {etapa.responsavel_nome ?? <span className="text-secondary fst-italic">sem responsável</span>}
              {quando && <small className="text-secondary"> {quando}</small>}
              {eu && <span className="badge casaa-selo-destaque ms-2">você</span>}
            </p>
          </div>
          {prazoOuFim && (
            <div className="col-auto text-end">
              <p className="small text-secondary mb-0">{prazoOuFim.rotulo}</p>
              <p className={`mb-0 ${'vencida' in prazoOuFim && prazoOuFim.vencida ? 'text-danger' : ''}`}>
                {prazoOuFim.valor}
              </p>
            </div>
          )}
        </div>
        {corpo ? (
          <p className="text-secondary">{corpo}</p>
        ) : (
          <p className="text-secondary opacity-50 fs-4 lh-1" title="Ninguém explicou esta etapa ainda">…</p>
        )}
        {/* DE QUEM ELA DEPENDE, dito com nome. Dele, 05/09: *"essa questão de
            vínculo com tarefas anteriores não está bem explicada aí no
            sistema"*. O número diz o degrau; só o nome diz o que se espera. */}
        {esperando.length > 0 && (
          <p className="small text-secondary mb-2">
            <i className="bi bi-hourglass-split me-1"></i>
            Espera {esperando.map((e) => `${e.numero} “${e.titulo}”`).join(' e ')}
          </p>
        )}

        <p className="pb-2">
          <span className={`badge badge-lg ${st.cor} me-1 mt-1`}>
            {st.rotulo}
          </span>
          {fila && (
            <span className={`badge badge-lg ${fila.cor} me-1 mt-1`}>{fila.rotulo}</span>
          )}
          {alertaDoPrazo(etapa) && (
            <span className="badge badge-lg casaa-selo-perigo me-1 mt-1">Prazo vencido</span>
          )}
        </p>

        {slotNoCard?.(etapa)}
      </div>
      <RodapeDoCard
        etapa={etapa}
        usuarios={usuarios}
        autorizacaoId={autorizacaoId}
        podeGerir={podeGerir}
        aoMudar={aoMudar}
        aoDerivar={aoDerivar}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * A LISTA
 * ═══════════════════════════════════════════════════════════════════════════ */
export function CustomEtapasLista({ etapas, slotDepoisDaLinha, ...resto }: Props) {
  if (etapas.length === 0) return null;
  const linhas = porLinha(etapas);
  const titulos = new Map(etapas.map((e) => [e.id, e.titulo]));
  const numeros = numerosDasEtapas(etapas);

  return (
    <div className="casaa-etapas">
      {linhas.map((linha, i) => {
        // Uma etapa ocupa a linha inteira; duas dividem ao meio; três ou mais
        // vão a três por linha no desktop e duas no tablet.
        const col =
          linha.length === 1 ? 'col-12'
            : linha.length === 2 ? 'col-12 col-md-6'
              : 'col-12 col-md-6 col-xl-4';
        return (
          <div key={i}>
            {/* A seta entre as linhas: some no celular, onde o número conta a
                história sozinho. */}
            {i > 0 && (
              <div className="casaa-etapas-seta" aria-hidden="true">
                <i className="bi bi-arrow-down"></i>
              </div>
            )}
            <div className="row gx-3 gx-lg-4">
              {linha.map((e, j) => (
                <div className={`${col} mb-3 mb-lg-4`} key={e.id}>
                  <Cartao
                    etapa={e}
                    numero={numeroDaEtapa(i, j, linha.length)}
                    esperando={
                      e.estado === 'aberta' && !e.na_vez
                        ? e.depende_de
                            .filter((id) => titulos.has(id))
                            .map((id) => ({
                              numero: numeros.get(id) ?? '',
                              titulo: titulos.get(id) as string,
                            }))
                        : []
                    }
                    {...resto}
                  />
                </div>
              ))}
            </div>
            {slotDepoisDaLinha?.(i, linha)}
          </div>
        );
      })}
    </div>
  );
}
